// Full-screen live preview for the Visual designer.
//
// Sends the current (unsaved) VisualModel to /api/visual-data/visual, which
// generates SQL from the SQLMODEL, runs it read-only against the project's
// preview DB, and returns the [schemaRow, ...rows] dataset + layout. The
// dataset is rendered with the shared <InsightVisual> host — the same vanilla-JS
// chart engine insight-bi uses — so the author sees a real chart from real data.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { X, Loader2, Play, Code2, Activity, Maximize2, Minimize2, RefreshCw } from 'lucide-react'

import toast from 'react-hot-toast'
import {
  isDocumentVisual, InsightVisual, readAutoRefreshSeconds, useAutoRefresh, UnderlyingDataModal } from '@twasta/visual-engine'
import type { VisualDataPayload, UnderlyingPayload } from '@twasta/visual-engine'
import {
  CriteriaBar, formatCriteriaValueForInput, requiredIssues,
  type CriteriaIssue, type VisualCriteriaPromptVar,
} from '@twasta/page-runtime/txn'
import {
  fetchVisualData, diagnoseVisual, makeVisualCriteriaRuntime, ROW_LIMIT_PARAM,
  type DiagnoseResult,
} from '@/api/visualData'
import { loadVisualObject } from '@/api/designers'
import PreviewLogMenu from '@/livepreview/runtime/PreviewLogMenu'
import type { VisualModel } from './types'
import { blankVisualModel, sanitizeVisualModel } from './helpers'
import { loadVisualCatalog } from './catalog'
import { VisualDocumentPreview } from './VisualDocumentPreview'
import { tw } from '@twasta/page-runtime'

// Remembers the prompt values entered per visual, so re-opening the preview
// (within the session) restores them instead of clearing the fields.
//
// Only values the user actually TYPED are remembered. A value that was merely
// seeded from a criterion's default is re-derived from the model on every run,
// so a default just changed in the Data Source tab is what the prompt shows
// next time instead of the previous session's seed. Each remembered value also
// records the criterion's authored default at the time it was remembered: when
// the author CHANGES that default the remembered value is dropped too, so the
// new default wins over what was typed against the old one.
interface RememberedParams {
  /** Values the user typed, keyed by prompt name. */
  values: Record<string, string>
  /** The authored default (promptVars[].default) each prompt had when its
      value was last seeded or typed. */
  defaults: Record<string, string>
}
const PARAM_CACHE = new Map<string, RememberedParams>()

/** Stable string form of a prompt's authored default — it can be a list (an IN
    chip list, a BETWEEN pair) — for comparing one run's default to the last. */
function defaultKey(raw: unknown): string {
  if (raw == null) return ''
  return Array.isArray(raw) ? raw.map((v) => String(v ?? '')).join('\u0001') : String(raw)
}

interface VisualPreviewModalProps {
  open: boolean
  onClose: () => void
  projectName: string
  objectName: string
  /** The current in-memory model — previewed without needing a save (used by
      the designer's Preview button). Omit to load the SAVED model from disk
      (used by the "View" action on a visual card). */
  model?: VisualModel
}

export default function VisualPreviewModal({
  open, onClose, projectName, objectName, model,
}: VisualPreviewModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          flex: 1, margin: 16, borderRadius: 8, overflow: 'hidden',
          background: 'var(--color-bg)', display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: '0.9125rem', fontWeight: 500, color: 'var(--color-text)' }}>
              Visual Preview
            </span>
            <span style={{ opacity: 0.5, fontSize: '0.8625rem' }}>—</span>
            <span style={{
              fontSize: '0.8625rem', color: 'var(--color-text-secondary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {projectName} / {objectName}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <PreviewLogMenu projectName={projectName} objectName={objectName} />
            <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close (Esc)" aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--color-bg)' }}>
          {/* key on the target so switching the previewed visual fully remounts
              the body — no stale data/params from the previous visual can bleed
              through while the new model's first run is in flight. With an
              in-memory model (designer Preview) render it directly; without one
              (card "View") load the saved model from disk. */}
          {model ? (
            <PreviewBody key={`${projectName}/${objectName}`} project={projectName} objName={objectName} model={model} cacheKey={`${projectName}/${objectName}`} />
          ) : (
            <ModalDiskPreview key={`disk:${projectName}/${objectName}`} project={projectName} objName={objectName} />
          )}
        </div>
      </div>
    </div>
  )
}

// Loads the SAVED visual model from disk (the workbench / card only know the
// object name) and returns it gated on the objName it was loaded FOR. Rendering
// must gate on this: when objName changes there is otherwise ONE render where a
// consumer would mount with the PREVIOUS visual's model and the NEW objName —
// firing the previous visual's query under the new obj (stale inline_model) and
// flashing the wrong chart. Shared by the workbench Live Preview pane and the
// card "View" modal so both load a visual exactly the same way.
export function useLoadedVisualModel(project: string, objName: string) {
  const [loaded, setLoaded] = useState<{ model: VisualModel; forObj: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    // Warm the visual catalog BEFORE sanitizing: sanitizeVisualModel rebuilds
    // LAYOUT_DATA.visualLayout from the catalog entry it resolves, so a cold
    // catalog (only the Tabular fallback) would rewrite a Calendar/chart into a
    // grid. The designer loads it the same way; loadVisualCatalog() is cached.
    Promise.all([loadVisualCatalog(), loadVisualObject(project, objName)])
      .then(([, data]) => {
        if (cancelled) return
        const model = data && data.content
          ? sanitizeVisualModel(data.content as VisualModel, objName)
          : blankVisualModel(objName)
        setLoaded({ model, forObj: objName })
      })
      .catch(() => { if (!cancelled) setError('Failed to load visual model') })
    return () => { cancelled = true }
  }, [project, objName])

  return { model: loaded && loaded.forObj === objName ? loaded.model : null, error }
}

// Disk-loaded body for the card "View" modal — same PreviewBody the designer
// Preview and the navigation pane use, so there is one preview surface.
function ModalDiskPreview({ project, objName }: { project: string; objName: string }) {
  const { model, error } = useLoadedVisualModel(project, objName)
  if (error) {
    return <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: 14 }}>{error}</div>
  }
  if (!model) {
    return (
      <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)', fontSize: 14 }}>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading preview…
      </div>
    )
  }
  return (
    <PreviewBody
      key={`${project}/${objName}`}
      project={project}
      objName={objName}
      model={model}
      cacheKey={`${project}/${objName}`}
    />
  )
}

// Embeddable, non-modal live preview for the workbench "Live Preview" tab.
// Loads the saved visual model from disk (the workbench only knows the object
// name) and renders the same chart body the modal uses.
export function VisualLivePreviewPane({ project, objName, initialParams }: {
  project: string; objName: string
  /** Prompt values to open with — an 'open' action button on a transaction
   *  passing values from the row it was clicked on. Treated as typed by the
   *  person, so the model's defaults do not overwrite them. */
  initialParams?: Record<string, unknown>
}) {
  const { model, error } = useLoadedVisualModel(project, objName)
  const [fullscreen, setFullscreen] = useState(false)

  // Full screen must sit ABOVE the runtime shell's sticky top bar (z 9000), or
  // the shell header paints over the top of the visual and hides its toolbar.
  const outerStyle: CSSProperties = fullscreen
    ? { position: 'fixed', inset: 0, zIndex: 9400, background: 'var(--color-bg)', display: 'flex', flexDirection: 'column' }
    : { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--color-bg)' }

  // No separate full-screen row — the toggle now lives in PreviewBody's action
  // bar next to Run (always visible), which frees the vertical space the row
  // used to take and lets the chart/feed use the full height.
  return (
    <div style={outerStyle}>
      {error ? (
        <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: 14 }}>{error}</div>
      ) : !model ? (
        <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)', fontSize: 14 }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading preview…
        </div>
      ) : (
        <PreviewBody
          key={`${project}/${objName}`}
          project={project}
          objName={objName}
          model={model}
          cacheKey={`${project}/${objName}`}
          fullscreen={fullscreen}
          onToggleFullscreen={() => setFullscreen((f) => !f)}
          initialParams={initialParams}
        />
      )}
    </div>
  )
}

interface PreviewBodyProps {
  project: string; objName: string; model: VisualModel; cacheKey: string
  fullscreen?: boolean; onToggleFullscreen?: () => void
  initialParams?: Record<string, unknown>
}

/**
 * The preview for one visual — a chart, or a set of documents.
 *
 * A Document visual's output is a laid-out document rather than anything
 * `InsightVisual` draws, so it takes a different body entirely: its own
 * criteria bar, its own run, its own pages. The dispatch is HERE rather than in
 * each of the three callers (the modal, the disk preview, the Live Preview
 * pane) so the three cannot disagree about which visuals are documents.
 *
 * `className` is what decides, because that is what every renderer already
 * dispatches on. The catalog's `OutputType` has never been read at render time
 * and making this the one place it suddenly mattered would put two answers in
 * the tree to one question.
 */
function PreviewBody(props: PreviewBodyProps) {
  const isDocument = isDocumentVisual(
    props.model?.LAYOUT_DATA?.visualLayout?.className,
    props.model?.LAYOUT_DATA?.visualLayout?.ID,
    props.model?.SQLMODEL?.VISUAL_NAME)
  if (isDocument) {
    return (
      <VisualDocumentPreview
        project={props.project}
        objName={props.objName}
        model={props.model}
      />
    )
  }
  return <ChartPreviewBody {...props} />
}

function ChartPreviewBody({
  project, objName, model, cacheKey, fullscreen = false, onToggleFullscreen, initialParams,
}: PreviewBodyProps) {
  const [data, setData] = useState<VisualDataPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showSql, setShowSql] = useState(false)
  const [diag, setDiag] = useState<DiagnoseResult | null>(null)
  const [diagLoading, setDiagLoading] = useState(false)
  // Monotonic run counter: each run()/runDiagnose() call claims a sequence
  // number and only commits its result if it's still the latest. Two runs can
  // race (rapid visual switching, auto-refresh overlapping a manual Run) and an
  // out-of-order response must NOT overwrite the newest chart/error.
  const runSeq = useRef(0)
  // Underlying-data drill-through: the client-side rows behind a double-clicked
  // point (or the ⋮ menu), or null when the modal is closed.
  const [underlying, setUnderlying] = useState<UnderlyingPayload | null>(null)
  // The criteria/filter area can be collapsed to reclaim vertical space for the
  // chart/feed; when collapsed it shows just the criteria count + first values.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false)
  // The bar's own required check on Run (the server's findings come with data).
  const [localIssues, setLocalIssues] = useState<CriteriaIssue[] | null>(null)
  // Prompt-variable values the user can edit, then re-run. Seeded from the
  // session cache, which holds only what the user TYPED — every other input is
  // re-seeded from the model's own defaults on each fetch (see PARAM_CACHE).
  const remembered = useMemo(() => {
    const cached = PARAM_CACHE.get(cacheKey)
    // Values passed in by an 'open' action button count as typed.
    const passed: Record<string, string> = {}
    for (const [k, v] of Object.entries(initialParams || {})) {
      if (v !== undefined && v !== null && v !== '') passed[k] = String(v)
    }
    return {
      defaults: { ...(cached?.defaults || {}) },
      /** Prompts the user typed into — their value survives a re-open. */
      edited: new Set([...Object.keys(cached?.values || {}), ...Object.keys(passed)]),
      initial: { ...(cached?.values || {}), ...passed },
    }
    // The passed values apply when the visual opens, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey])
  const [paramValues, setParamValues] = useState<Record<string, string>>(
    () => ({ ...remembered.initial }),
  )
  // Mirror of paramValues for the seeding pass in run(), which reads it after
  // an await (declared before the effect that fires the first run).
  const paramValuesRef = useRef(paramValues)
  useEffect(() => { paramValuesRef.current = paramValues }, [paramValues])

  // Persist the typed values, and the defaults they were typed against, so the
  // next open restores them.
  const persist = useCallback((values: Record<string, string>) => {
    const kept: Record<string, string> = {}
    for (const [name, val] of Object.entries(values)) {
      if (remembered.edited.has(name)) kept[name] = val
    }
    PARAM_CACHE.set(cacheKey, { values: kept, defaults: { ...remembered.defaults } })
  }, [cacheKey, remembered])

  // Transport for criteria F2 lookups + on-change item_change (previewing the
  // unsaved model, so the rules are read from inline_model).
  const criteriaRuntime = useMemo(
    () => makeVisualCriteriaRuntime({
      project, kind: 'visual', obj_name: objName, inline_model: model,
      notify: { success: toast.success, error: toast.error, info: (m) => toast(m) },
    }),
    [project, objName, model],
  )

  const runDiagnose = useCallback(async () => {
    const seq = ++runSeq.current
    setDiagLoading(true)
    try {
      const result = await diagnoseVisual({ project, obj_name: objName, inline_model: model, params: paramValues })
      if (seq !== runSeq.current) return
      setDiag(result)
    } catch (e) {
      if (seq !== runSeq.current) return
      setDiag(null)
      setError(extractError(e))
    } finally {
      if (seq === runSeq.current) setDiagLoading(false)
    }
  }, [project, objName, model, paramValues])

  const run = useCallback(
    // silent = an auto-refresh poll: no loading flash, and a failed poll keeps
    // the last good chart on screen instead of replacing it with an error.
    async (params?: Record<string, unknown>, userInitiated = false, silent = false) => {
      const seq = ++runSeq.current
      if (!silent) { setLoading(true); setError(null) }
      try {
        const payload = await fetchVisualData({
          project, obj_name: objName, inline_model: model, inline_model_for: objName, params,
        })
        // A newer run superseded this one (visual switched, or a manual Run
        // overtook an auto-refresh poll) — drop this stale response.
        if (seq !== runSeq.current) return
        setData(payload)
        setLocalIssues(null)
        // Refused by the criteria's checks: open the criteria at their reasons.
        if (payload.needsInput && payload.criteriaErrors?.length) setFiltersCollapsed(false)
        // Re-seed the prompt inputs from the model on EVERY fetch, so a
        // criterion's default the author has just changed in the Data Source
        // tab is what the prompt shows — a seeded value is never remembered
        // past the run that produced it. Values the user TYPED stand, unless
        // the criterion's authored default itself changed since they were
        // remembered: then the new default wins. Prefer the backend-RESOLVED
        // value (e.g. :calendar_year_start → a real date) over the raw token
        // default so a date input can actually display it.
        const promptList = payload.promptVars
        if (promptList?.length) {
          // Remembered values whose criterion's default has moved on.
          const stale = new Set<string>()
          for (const pv of promptList) {
            const authored = defaultKey(pv.default)
            const known = remembered.defaults[pv.name]
            if (remembered.edited.has(pv.name) && known !== undefined && known !== authored) {
              remembered.edited.delete(pv.name)
              stale.add(pv.name)
            }
            remembered.defaults[pv.name] = authored
          }
          const next = { ...paramValuesRef.current }
          let changed = false
          for (const name of stale) {
            if (name in next) { delete next[name]; changed = true }
          }
          for (const pv of promptList) {
            if (remembered.edited.has(pv.name) || stale.has(pv.name)) continue
            const resolved = payload.resolvedParams?.[pv.name]
            const raw = resolved != null && resolved !== '' ? resolved : pv.default
            const seed = formatCriteriaValueForInput(raw, pv.data_type)
            if (seed !== '' && seed !== next[pv.name]) { next[pv.name] = seed; changed = true }
          }
          if (changed) { paramValuesRef.current = next; setParamValues(next) }
          persist(next)
          if (stale.size) {
            // The dropped values were SENT with this fetch, so the payload
            // echoed them back as the resolved ones — re-run without them to
            // apply and display the criterion's new default.
            void runRef.current(Object.keys(next).length ? next : undefined, userInitiated, silent)
            return
          }
        }
        // Only collapse the criteria bar on an explicit Run that draws rows —
        // on entering Live Preview the criteria stay visible (with their
        // defaults) so the user can review, change, and Run.
        if (userInitiated && (payload.promptVars?.length ?? 0) > 0) {
          const drawn = !payload.needsInput && (payload.jsonData?.length ?? 0) > 1
          if (drawn) setFiltersCollapsed(true)
        }
      } catch (e) {
        if (seq !== runSeq.current) return
        if (!silent) {
          const msg = extractError(e)
          setError(msg)
        }
      } finally {
        if (!silent && seq === runSeq.current) setLoading(false)
      }
    },
    // model identity drives a refetch when the author edits and reopens.
    [project, objName, model, remembered, persist],
  )

  // Self-reference for the one re-fetch the seeding pass can need (a criterion
  // default changed under a remembered value).
  const runRef = useRef(run)
  useEffect(() => { runRef.current = run }, [run])

  // On open, run with the remembered values if present, else with defaults.
  useEffect(() => {
    void run(Object.keys(paramValues).length ? paramValues : undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run])

  // Auto-refresh (the visual's autoRefreshSeconds option): silently re-run
  // with the current criteria so the chart stays live without a loading flash.
  // What the bar shows under its criteria: the bar's own required check, else
  // the last run's findings (reasons it was refused, or notes on one that ran).
  const issues = useMemo<CriteriaIssue[]>(
    () => localIssues ?? [...(data?.criteriaErrors || []), ...(data?.criteriaNotices || [])],
    [localIssues, data],
  )
  const runCriteria = () => {
    const local = requiredIssues(promptVars as unknown as VisualCriteriaPromptVar[], paramValues)
    if (local.length) {
      setLocalIssues(local)
      setFiltersCollapsed(false)
      return
    }
    void run(paramValues, true)
  }
  const refreshSeconds = data && !data.needsInput
    ? readAutoRefreshSeconds(data.optionsArr, data.visualLayout)
    : 0
  useAutoRefresh(refreshSeconds, () => run(paramValues, false, true))

  const promptVars = data?.promptVars ?? []
  const visualName = useMemo(
    () => model?.LAYOUT_DATA?.visualLayout?.VisualName
      || model?.LAYOUT_DATA?.visualLayout?.ID || 'visual',
    [model],
  )

  // Test hook: the UI acceptance executor's `visual_has_data` reads state/rows.
  const twRows = data && !data.needsInput
    ? (data.rowCount ?? Math.max(0, (data.jsonData?.length ?? 1) - 1)) : undefined
  const twState = loading ? 'loading' : error ? 'error'
    : !data ? 'idle' : data.needsInput ? 'needs_input' : twRows ? 'ready' : 'empty'
  return (
    <div {...tw('screen', objName, { kind: 'V', state: twState, rows: twRows })}
         style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Shared criteria bar: collapse toggle + collapsed summary on the left,
          the editable criteria FORM below when expanded; Run / SQL / full-screen
          / auto-refresh / row-count go in the actions slot on the right. */}
      <CriteriaBar
        project={project}
        promptVars={promptVars as unknown as VisualCriteriaPromptVar[]}
        values={paramValues}
        onChange={(name, val) => {
          remembered.edited.add(name)
          const next = { ...paramValuesRef.current, [name]: val }
          paramValuesRef.current = next
          setParamValues(next)
          persist(next)
        }}
        onSubmit={runCriteria}
        runtime={criteriaRuntime}
        collapsed={filtersCollapsed}
        onToggleCollapsed={() => setFiltersCollapsed((c) => !c)}
        issues={issues}
        actions={(
          <>
            <button
              className="btn btn-primary btn-sm"
              onClick={runCriteria}
              disabled={loading}
              {...tw('action', 'apply')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Play size={14} /> {loading ? 'Running…' : 'Run'}
            </button>
            {onToggleFullscreen && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={onToggleFullscreen}
                title={fullscreen ? 'Exit full screen' : 'Full screen'}
                aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
                style={{ display: 'inline-flex', alignItems: 'center' }}
              >
                {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
            )}
            {/* SQL toggle only while the criteria are expanded. */}
            {data?.sourceSql && !filtersCollapsed && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowSql((s) => !s)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                title="Show the generated SQL"
              >
                <Code2 size={14} /> SQL
              </button>
            )}
            {refreshSeconds > 0 && (
              <span
                title={`Auto-refreshing every ${refreshSeconds}s`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 13, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap',
                }}
              >
                <RefreshCw size={12} /> {refreshSeconds}s
              </span>
            )}
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
              {data ? `${data.rowCount ?? Math.max(0, (data.jsonData?.length ?? 1) - 1)} rows` : ''}
            </span>
          </>
        )}
      />

      {showSql && data?.sourceSql && !filtersCollapsed && (
        <div style={{ borderBottom: '1px solid var(--color-border)' }}>
          {/* Diagnose now lives inside the SQL window. */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            padding: '6px 14px', background: 'var(--color-bg-secondary)',
          }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => void runDiagnose()}
              disabled={diagLoading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Count rows at each stage (table → after join → after filter) to find where rows are lost"
            >
              <Activity size={14} /> {diagLoading ? 'Diagnosing…' : 'Diagnose'}
            </button>
          </div>
          <pre style={{
            margin: 0, padding: '10px 14px', fontSize: 13, lineHeight: 1.5,
            background: 'var(--color-bg-tertiary, var(--color-bg-secondary))',
            color: 'var(--color-text)', overflowX: 'auto',
          borderBottom: '1px solid var(--color-border)', maxHeight: 200,
        }}>
          {data.sourceSql}
          {data.bindParams && Object.keys(data.bindParams).length > 0 && (
            `\n\n-- bound values:\n${
              Object.entries(data.bindParams)
                .map(([k, v]) => `--   ${k} = ${JSON.stringify(v)}`)
                .join('\n')
            }`
          )}
          </pre>
        </div>
      )}

      {diag && <DiagnosticsPanel diag={diag} onClose={() => setDiag(null)} />}

      {/* Chart area — scrolls on its own when the visual (e.g. a calendar with
          its event feed) is taller than the pane, so the action bar above stays
          visible while the content below scrolls into view. */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', padding: '8px 10px 10px', overflow: 'auto' }}>
        {loading && !data && <Centered><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</Centered>}
        {error && <Centered tone="error">{error}</Centered>}
        {!error && data?.needsInput && data.criteriaErrors?.length ? (
          <Centered tone="muted">
            Check the criteria above, then click <strong>&nbsp;Run</strong>.
          </Centered>
        ) : !error && data?.needsInput && (
          <Centered tone="muted">
            This visual needs input{promptVars.length ? ` (${promptVars.map((p) => p.label).join(', ')})` : ''}.
            Fill the field{promptVars.length === 1 ? '' : 's'} above and click <strong>&nbsp;Run</strong>.
          </Centered>
        )}
        {!error && data && !data.needsInput && (data.jsonData?.length ?? 0) > 1 && (
          <PreviewVisual
            jsonData={data.jsonData}
            visualLayout={data.visualLayout}
            optionsArr={data.optionsArr}
            onError={(e) => setError(e.message)}
            // Double-click a point (or the ⋮ "View underlying data") opens the
            // row-level detail; a single click is left for in-visual drill.
            onShowUnderlying={(payload) => setUnderlying(payload)}
            truncated={data.truncated}
            rowLimit={data.rowLimit}
            rowCeiling={data.rowCeiling}
            rowLimitAuthored={data.rowLimitAuthored}
            rowLimitFixed={data.rowLimitFixed}
            // Raising the limit is a RE-RUN with the viewer's number on the
            // existing prompt channel, not a new request shape — which is why
            // the criteria the run already had are carried with it.
            onRowLimit={(limit) => {
              const next = { ...paramValues, [ROW_LIMIT_PARAM]: String(limit) }
              setParamValues(next)
              void run(next, true)
            }}
          />
        )}
        {!loading && !error && data && !data.needsInput && (data.jsonData?.length ?? 0) <= 1 && (
          <Centered tone="muted">
            No rows for “{visualName}”. Check the parameter values applied
            (click <strong>&nbsp;SQL&nbsp;</strong> to see the bound dates/filters) —
            widen the range if the data falls outside it.
          </Centered>
        )}
      </div>

      {/* Underlying-data modal (opened from a chart double-click / ⋮ menu). The
          rows are computed client-side by the engine — no backend round-trip. */}
      {underlying && (
        <UnderlyingDataModal
          open
          jsonData={underlying.jsonData}
          visualLayout={underlying.visualLayout}
          filters={underlying.filters}
          onClose={() => setUnderlying(null)}
        />
      )}
    </div>
  )
}

// True when the visual's options enable the ⋮ toolbar menu — used to decide
// whether to reserve a header strip for it.
function visualHasMenu(optionsArr?: VisualDataPayload['optionsArr']): boolean {
  if (!Array.isArray(optionsArr)) return false
  return optionsArr.some((o) => {
    const name = String((o as { optionName?: string }).optionName || '').toLowerCase()
    if (!/toolbar/.test(name)) return false
    const oo = o as { checked?: unknown; defaultValue?: unknown }
    const v = oo.checked != null ? oo.checked : oo.defaultValue
    return v === true || String(v).toLowerCase() === 'true'
  })
}

// Renders the previewed visual with its ⋮ menu docked into a header strip
// (when the toolbar option is on) instead of floating over the chart.
function PreviewVisual({
  jsonData, visualLayout, optionsArr, onError, onShowUnderlying,
  truncated, rowLimit, rowCeiling, rowLimitAuthored, rowLimitFixed, onRowLimit,
}: {
  jsonData: VisualDataPayload['jsonData']
  visualLayout: VisualDataPayload['visualLayout']
  optionsArr: VisualDataPayload['optionsArr']
  onError: (e: Error) => void
  onShowUnderlying?: (payload: UnderlyingPayload) => void
  truncated?: boolean
  rowLimit?: number | null
  rowCeiling?: number | null
  rowLimitAuthored?: boolean
  rowLimitFixed?: boolean
  onRowLimit?: (limit: number) => void
}) {
  const [menuSlot, setMenuSlot] = useState<HTMLElement | null>(null)
  const hasMenu = visualHasMenu(optionsArr)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {hasMenu && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: 24, paddingBottom: 2, flexShrink: 0 }}>
          <span ref={setMenuSlot} style={{ display: 'inline-flex', alignItems: 'center' }} />
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>
        <InsightVisual
          jsonData={jsonData}
          visualLayout={visualLayout}
          optionsArr={optionsArr}
          chromeHost={hasMenu ? menuSlot : null}
          style={{ height: '100%', width: '100%' }}
          onError={onError}
          onShowUnderlying={onShowUnderlying}
          truncated={truncated}
          rowLimit={rowLimit}
          rowCeiling={rowCeiling}
          rowLimitAuthored={rowLimitAuthored}
          rowLimitFixed={rowLimitFixed}
          onRowLimit={onRowLimit}
        />
      </div>
    </div>
  )
}

// Counts at each query stage + a plain-language verdict on where rows are lost.
function DiagnosticsPanel({ diag, onClose }: { diag: DiagnoseResult; onClose: () => void }) {
  const cell = (c: { rows?: number; error?: string } | null | undefined): ReactNode => {
    if (!c) return <span style={{ opacity: 0.6 }}>—</span>
    if (c.error) return <span style={{ color: 'var(--pg-error, #c33)' }} title={c.error}>error</span>
    return <strong>{c.rows ?? 0}</strong>
  }

  const verdict = ((): string => {
    if (diag.needsInput) {
      return `Final count skipped — supply ${diag.missingParams.join(', ')} and Run first.`
    }
    const empty = diag.tables.filter((t) => t.rows === 0)
    const afterJoin = diag.afterJoinRows?.rows
    const result = diag.resultRows?.rows
    if (empty.length) {
      return `Table(s) ${empty.map((t) => t.table).join(', ')} have 0 rows — an INNER JOIN to an empty/unpopulated master drops everything. Populate them or use a LEFT JOIN.`
    }
    if (afterJoin === 0) {
      return 'The FROM/JOIN returns 0 rows — the join keys don’t match (no overlapping values). Check the ON condition or use a LEFT JOIN.'
    }
    if (result === 0 && (afterJoin ?? 0) > 0) {
      return 'Rows exist after the join but the WHERE filter removes them all — widen the date range / loosen the criteria (click SQL to see the bound values).'
    }
    if ((result ?? 0) > 0) {
      return `Looks healthy — ${result} row(s) after filtering. If the chart is still blank, check the column→axis mapping.`
    }
    return 'See the counts above to locate where rows drop to zero.'
  })()

  return (
    <div style={{
      padding: '10px 14px', borderBottom: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)', fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>Row diagnostics</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose} title="Hide"><X size={13} /></button>
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 4 }}>Tables (unfiltered)</div>
          <div style={{ display: 'flex', gap: 12 }}>
            {diag.tables.length === 0 && <span style={{ opacity: 0.6 }}>none detected</span>}
            {diag.tables.map((t) => (
              <span key={t.table} style={{ fontFamily: 'monospace' }}>
                {t.table}: {cell(t)}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 4 }}>After join</div>
          {cell(diag.afterJoinRows)}
        </div>
        <div>
          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 4 }}>After filter (result)</div>
          {cell(diag.resultRows)}
        </div>
      </div>
      <div style={{ marginTop: 8, color: 'var(--color-text)', lineHeight: 1.5 }}>{verdict}</div>

      {diag.sample.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 4 }}>
            Sample rows (unfiltered, after join) — check the actual date values/format:
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 13, fontFamily: 'monospace' }}>
              <thead>
                <tr>
                  {Object.keys(diag.sample[0]).map((k) => (
                    <th key={k} style={{ textAlign: 'left', padding: '3px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {diag.sample.map((row, i) => (
                  <tr key={i}>
                    {Object.keys(diag.sample[0]).map((k) => (
                      <td key={k} style={{ padding: '3px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text)', whiteSpace: 'nowrap' }}>
                        {row[k] == null ? '' : String(row[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function Centered({ children, tone }: { children: ReactNode; tone?: 'error' | 'muted' }) {
  const color = tone === 'error'
    ? 'var(--pg-error, #c33)'
    : 'var(--color-text-secondary)'
  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 8, padding: 24, textAlign: 'center',
      color, fontSize: 14,
    }}>
      {children}
    </div>
  )
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { detail?: unknown; error?: { message?: string } } } }).response
    const detail = resp?.data?.detail
    if (typeof detail === 'string') return detail
    if (resp?.data?.error?.message) return resp.data.error.message
  }
  return e instanceof Error ? e.message : String(e)
}
